export const smartFarmHero = {
  eyebrow: '智慧农业解决方案',
  title: '让每一块农田看得见',
  subtitle: '让每一份农产品卖得好',
  desc: '华辉数智以物联网、人工智能、ePin 数字身份、数字货架与供应链协同能力，帮助农业经营主体连接生产、品质、流通与消费市场，建设从田间到餐桌的智慧农场。',
  note: '不止监测农田，更帮助农场建立可感知、可管理、可追溯、可销售、可持续优化的数字化经营体系。',
  primaryLabel: '获取解决方案',
  secondaryLabel: '预约产品演示',
  tags: ['农田感知', 'AI 农事', 'ePin 追溯', '数字货架', '供应链协同', '经营数据'],
};

export interface PainPoint {
  id: string;
  icon: string;
  title: string;
  desc: string;
}

export const painPoints: PainPoint[] = [
  {
    id: 'P1',
    icon: 'ri-radar-line',
    title: '农情信息分散',
    desc: '土壤、气象、水肥、作物长势和设备状态无法统一管理，异常发现与处置依赖人工经验。',
  },
  {
    id: 'P2',
    icon: 'ri-file-shield-2-line',
    title: '产品品质难以证明',
    desc: '种植、采收、检测、加工、仓储与运输信息分散，优质农产品难以建立可信、清晰的品质表达。',
  },
  {
    id: 'P3',
    icon: 'ri-arrow-left-right-line',
    title: '产销两端缺少协同',
    desc: '农场生产节奏与市场需求脱节，容易出现先种后卖、库存积压、渠道不稳和供需波动等问题。',
  },
  {
    id: 'P4',
    icon: 'ri-route-line',
    title: '流通链条过长',
    desc: '产品经过多次装卸、分拣和转运，损耗、效率与品质控制压力持续增加。',
  },
  {
    id: 'P5',
    icon: 'ri-database-2-line',
    title: '经营数据无法沉淀',
    desc: '订单、库存、客户、渠道和服务数据分散，管理者难以形成有效的经营判断与持续优化。',
  },
];

export interface FlowStep {
  no: string;
  icon: string;
  title: string;
  desc: string;
}

export const solutionFlow: FlowStep[] = [
  { no: '01', icon: 'ri-radar-line', title: '农田物联感知', desc: '连接土壤、气象与作物长势数据' },
  { no: '02', icon: 'ri-robot-2-line', title: 'AI 农事管理', desc: '智能问答、任务提醒与农事辅助' },
  { no: '03', icon: 'ri-clipboard-line', title: '标准化生产与品质检测', desc: '关键环节标准记录与品控' },
  { no: '04', icon: 'ri-qr-scan-2-line', title: 'ePin 一物一码数字身份', desc: '一品一标、一物一码追溯' },
  { no: '05', icon: 'ri-store-2-line', title: '数字货架与产品上行', desc: '标准化数字商品与订单承接' },
  { no: '06', icon: 'ri-truck-line', title: '智能物流与供应链履约', desc: '分拣、仓储、配送协同' },
  { no: '07', icon: 'ri-line-chart-line', title: '经营数据回流与生产优化', desc: '消费数据反馈优化生产' },
];

export const solutionFlowSummary =
  '生产数据服务品质，品质数据支撑品牌，消费数据反馈产地，让农场逐步实现以需定产、以质增值、以数运营。';

export interface Capability {
  no: string;
  icon: string;
  title: string;
  desc: string;
  keywords: string[];
}

export const capabilities: Capability[] = [
  {
    no: '01',
    icon: 'ri-radar-line',
    title: '农田物联感知',
    desc: '连接土壤、气象、水肥、作物长势及设施环境等数据，建立农场数字底图，帮助管理者实时掌握基地运行状态。',
    keywords: ['环境监测', '地块管理', '设备状态', '异常预警'],
  },
  {
    no: '02',
    icon: 'ri-robot-2-line',
    title: 'AI 农事管理',
    desc: '通过 TOMA 智能体连接农情数据、种植规范和农事台账，提供智能问答、任务提醒、异常提示和农事辅助。',
    keywords: ['农事提醒', '智能问答', '知识沉淀', '经营辅助'],
  },
  {
    no: '03',
    icon: 'ri-clipboard-line',
    title: '标准化品质管理',
    desc: '围绕种植、采收、检测、加工和入库等关键环节建立标准记录，帮助农场提升产品质量稳定性与管理规范性。',
    keywords: ['生产台账', '批次管理', '品质检测', '标准作业'],
  },
  {
    no: '04',
    icon: 'ri-qr-scan-2-line',
    title: 'ePin 数字身份与追溯',
    desc: '为产品、批次和包装建立「一品一标、一物一码」的数字身份，支持记录产品来源、生产过程、检测信息和流通轨迹。',
    keywords: ['一物一码', '扫码溯源', '产品建档', '品质证明'],
  },
  {
    no: '05',
    icon: 'ri-store-2-line',
    title: '数字货架与产品上行',
    desc: '将农产品转化为标准化数字商品，支持内容展示、产品上架、订单承接、库存协同和多渠道销售。',
    keywords: ['产品展示', '订单管理', '渠道协同', '品牌传播'],
  },
  {
    no: '06',
    icon: 'ri-funds-box-line',
    title: '经营数据与供应链协同',
    desc: '连接订单、库存、履约、渠道和客户数据，为产能安排、商品结构、供应链协同和经营决策提供依据。',
    keywords: ['数据看板', '库存协同', '订单履约', '经营分析'],
  },
];

export interface LifecycleStep {
  no: string;
  title: string;
  desc: string;
}

export const lifecycle: LifecycleStep[] = [
  { no: '01', title: '农田在线', desc: '完成基地、地块、作物、设备、人员和农事记录的数字化建档。' },
  { no: '02', title: '品质在线', desc: '建立种植、采收、检测、加工、包装等关键环节的标准记录，让产品质量有据可查。' },
  { no: '03', title: '产品在线', desc: '为重点农产品建立 ePin 数字身份，形成产品档案、批次信息与扫码追溯入口。' },
  { no: '04', title: '市场在线', desc: '通过数字货架连接展示、订单、库存和渠道，让优质产品更高效进入消费场景。' },
  { no: '05', title: '经营在线', desc: '生产、品质、流通和消费数据持续回流，辅助优化种植计划、产品结构和经营策略。' },
];

export const lifecycleSummary =
  '数据从农田产生，在市场中验证，并反馈下一轮生产，让农业经营形成持续优化的闭环。';

// 数字身份与品质追溯
export const traceability = {
  title: '一物一码，让品质看得见',
  desc: 'ePin 数字身份为农产品建立可识别、可追溯、可管理的数字档案。消费者扫码可查看产品来源、批次信息、生产过程、检测记录与流通信息；农场则可通过统一档案提升质量管理和品牌表达能力。',
  note: '让优质农产品不再只靠口碑证明，而是以可验证的信息建立消费信任。',
  product: {
    name: '示例：有机大米',
    origin: '浙江 · 某示范农场',
    batch: 'B2024-0912-01',
    harvest: '2024 年 9 月采收',
    test: '品质检测 · 已通过',
    pack: '加工包装 · 已记录',
    logistic: '仓储物流 · 已记录',
  },
  scanSteps: [
    { title: '田间记录', desc: '种植、施肥、用药等农事全程留痕', icon: 'ri-plant-line' },
    { title: '品控记录', desc: '采收、检测、加工环节标准记录', icon: 'ri-file-shield-2-line' },
    { title: '物流记录', desc: '出库、转运、配送节点可查询', icon: 'ri-truck-line' },
    { title: '消费者扫码', desc: '扫码即见产品完整数字档案', icon: 'ri-qr-scan-2-line' },
  ],
};

// 数字物流与供应链协同
export const supplyChain = {
  title: '让农产品流通更少损耗、更高效率',
  desc: '智慧农场将产品批次、订单、分拣、包装、仓储和配送信息统一管理，帮助农业经营主体建立清晰的履约流程与供应链协同机制。',
  note: '让产品流、信息流和订单流同步运行，提升农产品从产地到消费端的协同效率。',
  nodes: [
    { title: '产地', desc: '产品出库建档', icon: 'ri-plant-line' },
    { title: '分拣', desc: '批次化分拣', icon: 'ri-box-3-line' },
    { title: '仓储', desc: '库存协同管理', icon: 'ri-archive-line' },
    { title: '配送', desc: '可追踪物流', icon: 'ri-truck-line' },
    { title: '消费端', desc: '收货与溯源', icon: 'ri-home-4-line' },
  ],
  capabilities: [
    {
      title: '批次化分拣',
      desc: '以产品和订单为基础进行分拣管理，减少人工核对与信息断层。',
      icon: 'ri-box-3-line',
    },
    {
      title: '可追踪物流',
      desc: '产品从产地出库到消费者收货，关键流通节点可进行记录与查询。',
      icon: 'ri-truck-line',
    },
    {
      title: '标准化包装',
      desc: '支持产品包装、物流单元和数字身份关联，提升流通过程识别与管理效率。',
      icon: 'ri-inbox-line',
    },
    {
      title: '多渠道库存协同',
      desc: '连接农场、仓储、门店和线上渠道库存，辅助库存预警和订单履约。',
      icon: 'ri-store-2-line',
    },
  ],
};

// AI 农事与经营助手
export const aiAssistant = {
  title: '让 AI 成为农场的数字助手',
  desc: 'TOMA 智能体可作为农场的统一智能服务入口，连接农情数据、生产台账、产品信息和经营数据，为农场人员提供更便捷的日常管理支持。',
  note: 'AI 不替代农业经验，而是帮助经验沉淀、流程在线、决策更及时。',
  chat: [
    { role: 'user', text: '今日待完成农事' },
    {
      role: 'ai',
      text: '今天有 3 项农事任务：2 号地块施肥、3 号温室温控巡检、A 批次产品检测送样。',
    },
    { role: 'user', text: '该批次产品检测记录' },
    {
      role: 'ai',
      text: 'B2024-0912 批次已完成采收检测，品质合格，检测报告已归档。',
    },
    { role: 'user', text: '本周订单情况' },
    {
      role: 'ai',
      text: '本周共 128 笔订单，其中 96 笔已发货，库存预警：有机大米剩余量偏低。',
    },
  ],
  scenarios: [
    { title: '农事提醒', desc: '根据农事计划与基地状态，提醒关键生产任务与待办事项。', icon: 'ri-alarm-line' },
    { title: '数据问答', desc: '围绕地块、产品、订单、库存和品质记录进行自然语言查询。', icon: 'ri-question-answer-line' },
    { title: '异常提示', desc: '对设备、环境、库存或订单履约中的异常情况提供提醒。', icon: 'ri-alert-line' },
    { title: '内容辅助', desc: '辅助生成产品介绍、溯源说明、渠道推广文案和基础运营素材。', icon: 'ri-file-text-line' },
    { title: '经营建议', desc: '基于已有生产和经营记录，为产品结构、库存安排和渠道运营提供参考。', icon: 'ri-lightbulb-line' },
  ],
};

export interface Scenario {
  id: string;
  title: string;
  desc: string;
  image: string;
}

export const scenarios: Scenario[] = [
  {
    id: 'S1',
    title: '标准农田',
    desc: '支持粮食作物生产监测、农事管理、品质记录与产销协同。',
    image:
      'https://readdy.ai/api/search-image?query=Expansive%20standardized%20grain%20farmland%20stretching%20to%20the%20horizon%20with%20orderly%20crop%20rows%2C%20modern%20IoT%20soil%20moisture%20sensors%20and%20weather%20monitoring%20poles%20installed%20along%20the%20field%20edges%2C%20soft%20golden%20morning%20sunlight%2C%20light%20blue%20sky%20with%20sparse%20clouds%2C%20aerial%20perspective%2C%20clean%20natural%20green%20and%20earth%20tones%2C%20high%20detail%20professional%20agriculture%20landscape%20photography&width=800&height=520&seq=smart-farm-scenario-01&orientation=landscape',
  },
  {
    id: 'S2',
    title: '设施农业',
    desc: '支持温室环境监测、设备联动、种植过程记录与异常预警。',
    image:
      'https://readdy.ai/api/search-image?query=Bright%20modern%20glass%20greenhouse%20facility%20with%20neat%20rows%20of%20fresh%20leafy%20green%20vegetables%20growing%20under%20controlled%20climate%2C%20automated%20irrigation%20pipes%20and%20climate%20control%20sensors%20visible%2C%20soft%20diffused%20natural%20daylight%20through%20the%20transparent%20roof%2C%20clean%20and%20organized%20planting%20environment%2C%20professional%20agriculture%20photography%2C%20high%20detail&width=800&height=520&seq=smart-farm-scenario-02&orientation=landscape',
  },
  {
    id: 'S3',
    title: '果园与茶园',
    desc: '支持作物长势管理、质量追溯、品牌包装与渠道销售。',
    image:
      'https://readdy.ai/api/search-image?query=Lush%20green%20orchard%20and%20tea%20garden%20on%20gentle%20rolling%20hills%20with%20neatly%20trimmed%20tea%20bushes%20and%20fruit%20trees%20bearing%20ripe%20fruits%2C%20warm%20golden%20sunlight%20filtering%20through%20the%20leaves%20creating%20soft%20shadows%2C%20vibrant%20natural%20green%20tones%2C%20peaceful%20rural%20atmosphere%2C%20professional%20agriculture%20landscape%20photography%2C%20high%20detail&width=800&height=520&seq=smart-farm-scenario-03&orientation=landscape',
  },
  {
    id: 'S4',
    title: '农业合作社',
    desc: '支持多农户、多基地、多产品的统一标准、统一品控与协同运营。',
    image:
      'https://readdy.ai/api/search-image?query=Organized%20agricultural%20cooperative%20scene%20with%20local%20farmers%20working%20together%20sorting%20and%20packing%20freshly%20harvested%20vegetables%20in%20a%20clean%20modern%20rural%20facility%2C%20wooden%20crates%20filled%20with%20colorful%20produce%2C%20natural%20daylight%20through%20large%20windows%2C%20community%20teamwork%20atmosphere%2C%20professional%20documentary%20photography%2C%20high%20detail&width=800&height=520&seq=smart-farm-scenario-04&orientation=landscape',
  },
  {
    id: 'S5',
    title: '农业产业园',
    desc: '支持园区级生产管理、供应链协同、产品品牌与经营数据管理。',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20agricultural%20industrial%20park%20integrating%20glass%20greenhouses%2C%20digital%20control%20center%20building%20and%20clean%20logistics%20area%2C%20technology%20and%20nature%20harmoniously%20combined%2C%20neat%20landscaping%20with%20green%20trees%2C%20soft%20natural%20light%2C%20clean%20modern%20architectural%20style%2C%20professional%20photography%2C%20high%20detail&width=800&height=520&seq=smart-farm-scenario-05&orientation=landscape',
  },
  {
    id: 'S6',
    title: '县域特色产业带',
    desc: '支持区域公用品牌建设、农产品数字化上行与产业数据沉淀。',
    image:
      'https://readdy.ai/api/search-image?query=Vast%20county%20level%20agricultural%20industry%20belt%20landscape%20with%20specialty%20crop%20fields%20and%20regional%20branded%20produce%20packaging%20area%2C%20digital%20supply%20chain%20network%20connecting%20farms%20to%20market%2C%20rolling%20green%20hills%20and%20connected%20roads%20in%20the%20background%2C%20natural%20light%2C%20professional%20photography%2C%20high%20detail&width=800&height=520&seq=smart-farm-scenario-06&orientation=landscape',
  },
];

export interface RoadmapStep {
  no: string;
  phase: string;
  title: string;
  content: string;
  result: string;
}

export const roadmap: RoadmapStep[] = [
  {
    no: '01',
    phase: '第一阶段',
    title: '基础数字化',
    content: '完成基地调研、地块建档、产品梳理、人员角色及经营台账建设。',
    result: '农场基础数据可管理，重点产品可建档。',
  },
  {
    no: '02',
    phase: '第二阶段',
    title: '生产与品控在线化',
    content: '部署适配的农情监测、农事管理和品质记录能力，建立关键生产环节的标准化记录。',
    result: '农事过程可追踪，产品批次可管理。',
  },
  {
    no: '03',
    phase: '第三阶段',
    title: '产品与渠道数字化',
    content: '接入 ePin 数字身份和数字货架，完善商品展示、订单、库存、履约和溯源能力。',
    result: '产品可追溯、可展示、可承接订单。',
  },
  {
    no: '04',
    phase: '第四阶段',
    title: '数据驱动运营',
    content: '通过生产、品质、销售和消费数据持续优化产能、产品结构、渠道和服务策略。',
    result: '经营决策逐步由经验驱动转向数据辅助。',
  },
];

export interface ValueProp {
  icon: string;
  audience: string;
  desc: string;
}

export const valueProps: ValueProp[] = [
  {
    icon: 'ri-plant-line',
    audience: '对农场',
    desc: '减少管理盲区，提升生产效率和品质稳定性，让种植过程更加规范、透明、可控。',
  },
  {
    icon: 'ri-community-line',
    audience: '对合作社与产业园',
    desc: '统一产品标准、品质记录、经营数据和协同流程，提升组织化经营能力。',
  },
  {
    icon: 'ri-customer-service-2-line',
    audience: '对消费者',
    desc: '通过产品数字身份了解农产品来源、生产过程和品质信息，增强消费信任。',
  },
  {
    icon: 'ri-government-line',
    audience: '对县域产业',
    desc: '推动农产品品牌化、流通数字化、产业协同化和价值在地化，助力县域特色产业发展。',
  },
];

export const consultation = {
  title: '让农业经营更有确定性',
  desc: '无论您是农业基地、合作社、产业园还是县域产业运营主体，华辉数智都可结合您的产业特点，提供从咨询规划、系统建设到运营支持的一体化智慧农场解决方案。',
  primaryLabel: '获取解决方案',
  secondaryLabel: '预约产品演示',
  formTitle: '提交需求 获取专属建议',
  success: '您的需求已提交，我们将尽快与您联系，为您提供适配的智慧农场建设建议。',
};

// 相关案例
export interface FarmCase {
  id: string;
  name: string;
  region: string;
  scene: string;
  status: '已验证' | '推进中';
  problem: string;
  solution: string;
  points: string[];
  href: string;
  featured?: boolean;
}

export const farmCases: FarmCase[] = [
  {
    id: 'FC-01',
    name: '中仙数智商贸',
    region: '县域 · 农贸流通场景',
    scene: '农产品数字化上行与品质追溯',
    status: '推进中',
    problem: '农产品来源与品质难证明，产销两端信息不对称，优质农产品难以获得应有的市场回报。',
    solution: '以 ePin 一物一码溯源为品质背书，接入数字货架实现产品上行，沉淀批次档案与经营数据。',
    points: ['一物一码溯源', '数字货架上行', '批次品质档案'],
    href: '#consultation',
    featured: true,
  },
  {
    id: 'FC-02',
    name: '大庄科样板间',
    region: '中国 · 北京 · 延庆',
    scene: '农产品上行与县域网络复制',
    status: '已验证',
    problem: '农产品销售难、信息不对称，乡村小店缺流量与资金。',
    solution: 'ePin 溯源 + 数字货架 + 信用进货 + 云社员协同，打通农产品上行与县域复制。',
    points: ['农产品上行', '信用进货', '云社员协同'],
    href: '/scale',
  },
  {
    id: 'FC-03',
    name: '帝星数商综合体',
    region: '中国 · 北京',
    scene: '数字商业与消费闭环',
    status: '已验证',
    problem: '存量会员缺乏数字经营入口，门店与消费数据割裂，复购与传播难。',
    solution: '数字货架 + 消费卡 + TOMA 智能体 + 云创客协同，验证数字商业完整闭环。',
    points: ['数字货架', '消费卡', 'TOMA 智能体'],
    href: '/launch',
  },
];